import { Button, createStyles, FormControl, Grid, InputAdornment, makeStyles, TextField, Theme, Typography } from '@material-ui/core';
import * as React from 'react';
import { useState } from 'react';
import { BulkResponseDialog } from '../common/BulkResponseDialog';
import Icon, { SupportedIcons } from '../common/Icon';
import { PlanByLocationSelector } from '../common/Selector/PlanByLocationSelector';
import { AuthContext } from '../_auth/AuthContext';
import { CreditMemberships } from '../_service/bulk';
import { BulkCreditRequest, BulkUtilResponse } from '../_types/bulk';
import { ISearchResult } from '../_types/search';
import { ServicePlan } from '../_types/serviceplan';
import { ILocationSummary } from '../_types/user';
import { BulkShowResultsListButton } from '../common/BulkShowResultsListButton';

interface Props {
  plans: ServicePlan[],
}

const useStyles = makeStyles((theme: Theme) => 
    createStyles({
        mainContainer: {
          maxWidth: 700,
          borderRadius: 10,
          backgroundColor: '#fff',
          boxShadow: '2px 2px 8px rgb(50 50 93 / 20%)',
          marginTop: 20,
          padding: theme.spacing(2)
        },
        dateContainer: {
          marginTop: 10,
          marginBottom: 10
        },
        inputLabel: {
          fontSize: 14
        },
        icon: {
          width: 12,
          height: 12,
          cursor: 'pointer'
        }
    })
);

const CreditMembers: React.FC<Props> = (prop: Props) => {
    const { user } = React.useContext(AuthContext);
    const [location, setLocation] = useState<ILocationSummary | ISearchResult | null>(user.currentLocation || null);
    const [selectedPlan, setSelectedPlan] = useState<string | null>();
    const [days, setDays] = useState<number>(0);
    const [months, setMonths] = useState<number>(0);
    const [dollars, setDollars] = useState<number>(0);
    const [isCrediting, setIsCrediting] = useState<boolean>(false);
    const [isOpen, setIsOpen] = React.useState<boolean>(false);
    const [emailList, setEmailList] = useState<string[]>([]);
    const [ignoreList, setIgnoreList] = useState<string[]>([]);
    const [storedEmailList, setStoredEmailList] = useState<string[] | undefined>();
    const [storedIgnoreList, setStoredIgnoreList] = useState<string[] | undefined>();
    const classes = useStyles();

    const _submit = async() => {
      if (!location) return alert('Please select a location.');
      if (!months && !days && !dollars) return alert('Please select an input to credit.');

      setIsCrediting(true);
      let request: BulkCreditRequest = {
        service_location_id: location?.id,
        service_plan_id: (selectedPlan && selectedPlan !== 'all plans') ? Number(selectedPlan) : null,
        days: days,
        months: months,
        dollars: dollars
      };
      let response: BulkUtilResponse | null;

      try {
        response = await CreditMemberships(request);
      } catch(error) {
        setIsCrediting(false);
        return alert('Something went wrong while connecting to the API. Please try again.');
      }

      if (response !== null) {
        setIsCrediting(false);
        setSelectedPlan(null);

        if (response.emails && response.emails.length > 0) {
          setEmailList(response.emails);
          setIsOpen(true);
        }
        
        if (response.ignoreList && response.ignoreList.length > 0) {
          setIgnoreList(response.ignoreList);
          setIsOpen(true);
        }
  
        if (response.emails && response.emails.length === 0
          && response.ignoreList && response.ignoreList.length === 0) {
            return alert('No memberships were credited. Make sure you selected the correct location and/or plan.');
          }
      }
    }

    return (
      <Grid container
        className={classes.mainContainer}
        alignItems="center">
        <Grid item>
          <h3>Credit Members</h3>
          <p>Select a location and a plan to give gift credits to the members of a given plan. 
              You can select "All Plans" to credit all members at the given location. Only one input 
              (months | days | dollars) can be submitted at a time.</p>
        </Grid>
        <PlanByLocationSelector 
          onLocationChange={(newValue: ISearchResult | null) => {
              setLocation(newValue);
          }}
          onPlanChange={(event: any) => setSelectedPlan(event.target.value as string)}
          planValue={selectedPlan || ''} 
          plans={prop.plans}
        />
        <Grid container 
          className={classes.dateContainer}
          spacing={4}
          justify="space-evenly"
          direction="row">
          <Grid item sm={3} xs={12}>
            <FormControl>
              <Typography className={classes.inputLabel} 
                title="Enter the number of free months to credit. This field will honor decimals.">
                  Months
                  <Icon className={classes.icon} iconName={SupportedIcons.InfoOutlined} color="primary" />
              </Typography>
              <TextField variant="outlined"
                type="number"
                disabled={!!days || !!dollars}
                name="input"
                InputLabelProps={{ shrink: true }}
                onChange={(e) => { setMonths(Number(e.target.value)) }}
              />
            </FormControl>
          </Grid>
          <Grid item sm={3} xs={12}>
            <FormControl>
              <Typography className={classes.inputLabel} 
                title="Enter the number of days worth of credits to provide.">
                  Days
                  <Icon className={classes.icon} iconName={SupportedIcons.InfoOutlined} color="primary" />
              </Typography>
              <TextField variant="outlined"
                type="number"
                disabled={!!months || !!dollars}
                name="input"
                InputLabelProps={{ shrink: true }}
                onChange={(e) => { setDays(Number(e.target.value)) }}
              />
            </FormControl>
          </Grid>
          <Grid item sm={3} xs={12}>
            <FormControl>
              <Typography className={classes.inputLabel} 
                title="Enter the number of credits to provide.">
                  Dollars
                  <Icon className={classes.icon} iconName={SupportedIcons.InfoOutlined} color="primary" />
              </Typography>
              <TextField variant="outlined"
                type="number"
                disabled={!!months || !!days}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                    )
                }}
                name="input"
                onChange={(e) => { setDollars(Number(e.target.value)) }}
              />
            </FormControl>
          </Grid>
        </Grid>
        {isCrediting ? <p>Crediting. Please wait...this can take several minutes...</p> :
          <>
            <Grid item xs={12} sm={12} style={{ display: "flex", justifyContent: "flex-end", height: "2.85em", flexDirection: "column" }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => { _submit() }}>
                Submit
              </Button>
            </Grid>
            <Grid item xs={12} sm={12} style={{ display: "flex", justifyContent: "flex-start", flexDirection: "column" }}>
              <BulkShowResultsListButton
                title={"Successfully Credited " + (storedEmailList?.length || 0).toString() + " Member Emails"}
                emailList={storedEmailList}
                ignoreList={storedIgnoreList}
                hide={storedEmailList === undefined}
                csvName={"CreditMembers" + new Date().toLocaleDateString()}
              />
            </Grid>
          </>
        }
        {(emailList.length > 0 || ignoreList.length > 0) && <BulkResponseDialog 
              title="Successfully Credited Member Emails"
              isOpen={isOpen}
              emailList={emailList}
              ignoreList={ignoreList}
              handleClose={() => {
                setIsOpen(false);
                setStoredEmailList(emailList);
                setStoredIgnoreList(ignoreList);
                setEmailList([]);
                setIgnoreList([]);
              }} />}
      </Grid>
    )
}

export default CreditMembers;